import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import {
  getDownloadURL,
  ref as sRef,
  uploadBytes,
} from "firebase/storage";

import { auth, db, storage } from "../firebaseConfig";
import { resizeToJpeg } from "../utils/imageResize";

const COMMISSION = 0.15;
const MAX_CATS = 3;

function n(v) {
  const x = Number(v);
  return isFinite(x) ? x : null;
}

function uniq(arr) {
  return Array.from(new Set(arr.filter(Boolean)));
}

function safeTrim(v) {
  return typeof v === "string" ? v.trim() : "";
}

export default function ProductoFormPage() {
  const navigate = useNavigate();
  const { productId } = useParams();
  const isEdit = Boolean(productId);

  const uid = auth?.currentUser?.uid || null;

  // ======= producto =======
  const [titulo, setTitulo] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [esUsado, setEsUsado] = useState(false);
  const [garantia, setGarantia] = useState("");

  // precio = lo que recibe vendedor
  const [precioVendedor, setPrecioVendedor] = useState("");

  const precioV = useMemo(() => n(precioVendedor), [precioVendedor]);
  const comision = useMemo(() => (precioV == null ? null : precioV * COMMISSION), [precioV]);
  const precioVenta = useMemo(() => (precioV == null ? null : precioV * (1 + COMMISSION)), [precioV]);

  // ======= envío =======
  const [tamanoCm, setTamanoCm] = useState("");
  const [pesoKg, setPesoKg] = useState("");

  const [limiteLargo, setLimiteLargo] = useState(null);
  const [limitePeso, setLimitePeso] = useState(null);

  const tcm = useMemo(() => n(tamanoCm), [tamanoCm]);
  const pkg = useMemo(() => n(pesoKg), [pesoKg]);

  const excedeLimite = useMemo(() => {
    if (limiteLargo == null || limitePeso == null) return false;
    const exTam = tcm != null && tcm > limiteLargo;
    const exPes = pkg != null && pkg > limitePeso;
    return exTam || exPes;
  }, [tcm, pkg, limiteLargo, limitePeso]);

  const [envioGratisTienda, setEnvioGratisTienda] = useState(false);
  const [costoEnvioTienda, setCostoEnvioTienda] = useState("");
  const [nosotrosEnviamos, setNosotrosEnviamos] = useState(true);

  // envioTienda se deriva del excedeLimite
  const envioTienda = excedeLimite;
  useEffect(() => {
  if (excedeLimite && nosotrosEnviamos) {
    setNosotrosEnviamos(false);
  }
}, [excedeLimite, nosotrosEnviamos]);

  // ======= categorías =======
  const [cats, setCats] = useState([]);
  const [catSel, setCatSel] = useState([]);

  // ======= imagen principal =======
  const [principalFile, setPrincipalFile] = useState(null);
  const [principalPreview, setPrincipalPreview] = useState("");
  const [principalUrl, setPrincipalUrl] = useState("");
  const [miniUrl, setMiniUrl] = useState("");

  // ======= Variantes =======
  const [colorPresets] = useState(["Negro", "Blanco", "Rojo", "Azul", "Verde", "Rosa", "Gris", "Café"]);
  const [tallaPresets] = useState(["XS", "S", "M", "L", "XL", "XXL", "Única"]);

  const [selColors, setSelColors] = useState([]);
  const [selSizes, setSelSizes] = useState([]);
  const [customColor, setCustomColor] = useState("");
  const [customSize, setCustomSize] = useState("");

  // variants: [{key,id,color,talla,stock,files:[File], previews:[url], existingImages:[], existingMini:[]}]
  const [variants, setVariants] = useState([]);

  // ======= estados =======
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // ======= progreso guardado =======
  const [saveStep, setSaveStep] = useState("");
  const [saveProgress, setSaveProgress] = useState(0);

  function updateSaveProgress(progress, step) {
    setSaveProgress(progress);
    setSaveStep(step);
  }

  // ======= cargar límites + categorías =======
  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoading(true);

        // limites: informacion/tamano
        const limRef = doc(db, "informacion", "tamano");
        const limSnap = await getDoc(limRef);
        const lim = limSnap.exists() ? limSnap.data() : {};
        const L = Number(lim?.largo);
        const P = Number(lim?.peso);

        if (alive) {
          setLimiteLargo(isFinite(L) ? L : null);
          setLimitePeso(isFinite(P) ? P : null);
        }

        // categorias
        const cs = await getDocs(collection(db, "categorias"));
        const list = cs.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .map((x) => ({
            id: x.id,
            nombre: x.nombre || x.id,
            imagen: x.imagen || "",
          }))
          .sort((a, b) => String(a.nombre).localeCompare(String(b.nombre)));

        if (alive) setCats(list);

        // si edit: cargar producto + variantes
        if (isEdit) {
          const pRef = doc(db, "productos", productId);
          const pSnap = await getDoc(pRef);
          if (!pSnap.exists()) throw new Error("Producto no encontrado.");

          const p = pSnap.data() || {};
          if (uid && p.vendedorId && p.vendedorId !== uid) {
            throw new Error("No tienes permiso para editar este producto.");
          }

          if (!alive) return;

          setTitulo(p.titulo || "");
          setDescripcion(p.descripcion || "");
          setEsUsado(Boolean(p.usado ?? p.esUsado ?? false));
          setGarantia(p.garantia || "");
          setPrecioVendedor(p.precio != null ? String(p.precio) : "");
          setTamanoCm(p.tamano != null ? String(p.tamano) : "");
          setPesoKg(p.pesoKg != null ? String(p.pesoKg) : "");
          setEnvioGratisTienda(Boolean(p.envioGratisTienda));
          setCostoEnvioTienda(p.costoEnvioTienda != null ? String(p.costoEnvioTienda) : "");
          setNosotrosEnviamos(p.nosotrosEnviamos !== false); // null cuenta como true
          setCatSel(Array.isArray(p.categorias) ? p.categorias : []);
          setPrincipalUrl(p.imagenPrincipal || "");
          setMiniUrl(p.imagenMini || "");
          setPrincipalPreview(p.imagenPrincipal || "");

          const vSnap = await getDocs(collection(db, "productos", productId, "variantes"));
          const vList = vSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

          const mapped = vList.map((v) => ({
            id: v.id,
            key: v.id,
            color: v.color || "",
            talla: v.talla || "",
            stock: v.stock != null ? String(v.stock) : "",
            files: [],
            previews: Array.isArray(v.imagenesMini) ? [...v.imagenesMini] : [],
            existingImages: Array.isArray(v.imagenes) ? [...v.imagenes] : [],
            existingMini: Array.isArray(v.imagenesMini) ? [...v.imagenesMini] : [],
          }));

          setVariants(mapped);
          setSelColors(uniq(vList.map((x) => x.color)));
          setSelSizes(uniq(vList.map((x) => x.talla)));
        }
      } catch (e) {
        console.error(e);
        if (alive) setError(e.message || "No se pudo cargar.");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [isEdit, productId, uid]);

  // ======= UI helpers =======
  function toggleInList(value, list, setList) {
    setList((prev) => (prev.includes(value) ? prev.filter((x) => x !== value) : [...prev, value]));
  }

  function addCustomColor() {
    const c = customColor.trim();
    if (!c) return;
    if (!selColors.includes(c)) setSelColors((prev) => [...prev, c]);
    setCustomColor("");
  }

  function addCustomSize() {
    const s = customSize.trim();
    if (!s) return;
    if (!selSizes.includes(s)) setSelSizes((prev) => [...prev, s]);
    setCustomSize("");
  }

  function removeSelectedColor(value) {
    setSelColors((prev) => prev.filter((x) => x !== value));
  }

  function removeSelectedSize(value) {
    setSelSizes((prev) => prev.filter((x) => x !== value));
  }

  function generateVariants() {
    const colors = selColors.length ? selColors : [""];
    const sizes = selSizes.length ? selSizes : [""];

    const oldMap = new Map();
    for (const v of variants) {
      oldMap.set(v.key, v);
    }

    const comb = [];
    for (const c of colors) {
      for (const s of sizes) {
        const key = `${c || "_"}__${s || "_"}`;
        const old = oldMap.get(key);

        comb.push(
          old || {
            key,
            id: crypto.randomUUID(),
            color: c,
            talla: s,
            stock: "",
            files: [],
            previews: [],
            existingImages: [],
            existingMini: [],
          }
        );
      }
    }

    setVariants(comb);
  }

  function removeVariant(key) {
    setVariants((prev) => prev.filter((v) => v.key !== key));
  }

  function setVariantField(key, field, value) {
    setVariants((prev) => prev.map((v) => (v.key === key ? { ...v, [field]: value } : v)));
  }

  function addVariantFiles(key, fileList) {
    const files = Array.from(fileList || []);

    setVariants((prev) =>
      prev.map((v) => {
        if (v.key !== key) return v;

        const existingCount = (v.existingMini?.length || 0);
        const currentNewFiles = v.files || [];
        const room = Math.max(0, 5 - existingCount - currentNewFiles.length);
        const filesToAdd = files.slice(0, room);

        const newFiles = [...currentNewFiles, ...filesToAdd];
        const newPrev = [...(v.previews || []), ...filesToAdd.map((f) => URL.createObjectURL(f))];

        return { ...v, files: newFiles, previews: newPrev };
      })
    );
  }

  function removeVariantImage(key, idx) {
    setVariants((prev) =>
      prev.map((v) => {
        if (v.key !== key) return v;

        const existingMini = [...(v.existingMini || [])];
        const existingImages = [...(v.existingImages || [])];
        const files = [...(v.files || [])];
        const previews = [...(v.previews || [])];

        const existingCount = existingMini.length;

        if (idx < existingCount) {
          existingMini.splice(idx, 1);
          existingImages.splice(idx, 1);
          previews.splice(idx, 1);
        } else {
          const fileIdx = idx - existingCount;
          if (fileIdx >= 0 && fileIdx < files.length) {
            files.splice(fileIdx, 1);
          }
          previews.splice(idx, 1);
        }

        return {
          ...v,
          existingMini,
          existingImages,
          files,
          previews,
        };
      })
    );
  }

  async function uploadTwoSizes(file, bigPath, miniPath) {
    const bigBlob = await resizeToJpeg(file, 1200);
    const miniBlob = await resizeToJpeg(file, 256);

    const bigRef = sRef(storage, bigPath);
    const miniRef = sRef(storage, miniPath);

    await uploadBytes(bigRef, bigBlob, { contentType: "image/jpeg" });
    await uploadBytes(miniRef, miniBlob, { contentType: "image/jpeg" });

    const bigUrl = await getDownloadURL(bigRef);
    const miniUrl = await getDownloadURL(miniRef);

    return { bigUrl, miniUrl };
  }

  function validateBeforeSave() {
    if (!uid) return "Inicia sesión.";
    if (!titulo.trim()) return "Falta título.";
    if (!descripcion.trim()) return "Falta descripción.";
    if (precioV == null || precioV <= 0) return "Precio inválido (lo que recibe el vendedor).";
    if (catSel.length === 0) return "Elige al menos 1 categoría.";
    if (catSel.length > 3) return "Máximo 3 categorías.";
    if (!isEdit && !principalFile) return "Sube una imagen principal.";

    if (!nosotrosEnviamos || envioTienda) {
      if (!envioGratisTienda) {
        const ce = n(costoEnvioTienda);
        if (ce == null || ce < 0) return "Costo de envío inválido.";
      }
    }

    if (variants.length === 0) {
      return "Debes agregar al menos una variante.";
    }

    for (const v of variants) {
      const st = n(v.stock);
      const hasStock = st != null && st > 0;
      const imgCount = (v.files?.length || 0) + (v.existingImages?.length || 0);

      if (!hasStock) {
        return `Variante (${v.color || "sin color"} / ${v.talla || "sin talla"}) sin stock.`;
      }

      if (imgCount < 1) {
        return `Variante (${v.color || "sin color"} / ${v.talla || "sin talla"}) sin imagen.`;
      }
    }

    return "";
  }

  async function handleSave() {
    setError("");
    const msg = validateBeforeSave();
    if (msg) return setError(msg);

    setSaving(true);
    updateSaveProgress(5, "Conectando con base de datos...");

    try {
      const pRef = isEdit ? doc(db, "productos", productId) : doc(collection(db, "productos"));
      const pid = pRef.id;

      let finalPrincipalUrl = principalUrl;
      let finalMiniUrl = miniUrl;

      if (principalFile) {
        updateSaveProgress(18, "Ajustando imagen principal...");
        const up = await uploadTwoSizes(
          principalFile,
          `productos/${pid}/principal.jpg`,
          `productos/${pid}/principal_mini.jpg`
        );
        updateSaveProgress(32, "Subiendo imagen principal...");
        finalPrincipalUrl = up.bigUrl;
        finalMiniUrl = up.miniUrl;
      }

      updateSaveProgress(45, "Preparando datos del producto...");

      const payload = {
        vendedorId: uid,
        titulo: titulo.trim(),
        descripcion: descripcion.trim(),
        usado: Boolean(esUsado),
        garantia: garantia.trim(),
        precio: precioVenta,
        comision: comision ?? null,
        precioVenta: precioVenta ?? null,

        // ahora string
        tamano: safeTrim(tamanoCm) || "",
        pesoKg: safeTrim(pesoKg) || "",

        orden: 1,
        dificultadEnvio: 1,
        nosotrosEnviamos: excedeLimite ? false : Boolean(nosotrosEnviamos),
        envioTienda: Boolean(envioTienda),
        envioGratisTienda: Boolean(envioGratisTienda),
        costoEnvioTienda: (!nosotrosEnviamos || envioTienda)
          ? (envioGratisTienda ? 0 : n(costoEnvioTienda) ?? 0)
          : null,        
        categorias: catSel,
        imagenPrincipal: finalPrincipalUrl || "",
        imagenMini: finalMiniUrl || "",
        updatedAt: serverTimestamp(),
      };

      const coloresDisponibles = uniq(variants.map((v) => v.color).filter(Boolean));
      const tallasDisponibles = uniq(variants.map((v) => v.talla).filter(Boolean));
      payload.coloresDisponibles = coloresDisponibles.length ? coloresDisponibles : [];
      payload.tallasDisponibles = tallasDisponibles.length ? tallasDisponibles : [];

      if (!isEdit) payload.createdAt = serverTimestamp();

      updateSaveProgress(56, "Subiendo datos principales...");
      if (isEdit) await updateDoc(pRef, payload);
      else await setDoc(pRef, payload);

      const totalVariants = variants.length || 1;

      for (let vIndex = 0; vIndex < variants.length; vIndex++) {
        const v = variants[vIndex];
        const vid = v.id || crypto.randomUUID();

        let bigUrls = [...(v.existingImages || [])];
        let miniUrls = [...(v.existingMini || [])];

        if ((v.files || []).length > 0) {
          updateSaveProgress(
            65 + Math.round((vIndex / totalVariants) * 20),
            `Subiendo imágenes de variante ${vIndex + 1} de ${totalVariants}...`
          );
        }

        for (let i = 0; i < (v.files || []).length; i++) {
          const file = v.files[i];
          const uploadIndex = bigUrls.length;

          const { bigUrl, miniUrl } = await uploadTwoSizes(
            file,
            `productos/${pid}/variantes/${vid}/img_${uploadIndex}.jpg`,
            `productos/${pid}/variantes/${vid}/img_${uploadIndex}_mini.jpg`
          );

          bigUrls.push(bigUrl);
          miniUrls.push(miniUrl);
        }

        const vRef = doc(db, "productos", pid, "variantes", vid);
        await setDoc(
          vRef,
          {
            id: vid,
            color: v.color || "",
            talla: v.talla || "",
            stock: n(v.stock) ?? 0,
            imagenes: bigUrls,
            imagenesMini: miniUrls,
            updatedAt: serverTimestamp(),
            createdAt: serverTimestamp(),
          },
          { merge: true }
        );
      }

      updateSaveProgress(96, "Completando guardado...");
      updateSaveProgress(100, "Producto guardado correctamente.");

      setTimeout(() => {
        navigate("/misProductos");
      }, 350);
    } catch (e) {
      console.error(e);
      setError(e?.message || "No se pudo guardar el producto.");
    } finally {
      setTimeout(() => {
        setSaving(false);
        setSaveProgress(0);
        setSaveStep("");
      }, 250);
    }
  }

  async function handleDeleteProduct() {
    setError("");

    if (!isEdit) return;
    if (!uid) return setError("Inicia sesión.");

    const ok = window.confirm(
      "¿Estás seguro?\n\nSi se elimina ya no aparecerá a la venta y no lo podrás recuperar."
    );
    if (!ok) return;

    setSaving(true);
    updateSaveProgress(10, "Eliminando variantes...");

    try {
      const varsSnap = await getDocs(collection(db, "productos", productId, "variantes"));
      for (const d of varsSnap.docs) {
        await deleteDoc(d.ref);
      }

      updateSaveProgress(70, "Eliminando producto...");
      await deleteDoc(doc(db, "productos", productId));

      updateSaveProgress(100, "Producto eliminado.");
      navigate("/misProductos");
    } catch (e) {
      console.error(e);
      setError(e?.message || "No se pudo eliminar el producto.");
    } finally {
      setSaving(false);
      setSaveProgress(0);
      setSaveStep("");
    }
  }

  if (loading) {
    return (
      <div style={S.page}>
        <div style={S.card}>
          <div style={S.h1}>Cargando…</div>
          <div style={S.sub}>Preparando formulario.</div>
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <div style={S.card}>
        <div style={S.topRow}>
          <div>
            <div style={S.h1}>{isEdit ? "Editar producto" : "Crear producto"}</div>
            <div style={S.sub}>Simple y rápido. Primero lo esencial; luego variantes.</div>
          </div>

          <button
            style={S.secondaryBtn}
            type="button"
            onClick={() => navigate("/misProductos")}
            disabled={saving}
          >
            Volver
          </button>
        </div>

        <Section title="Datos del producto">
          <Row2>
            <Field label="Título *">
              <input
                style={S.input}
                value={titulo}
                onChange={(e) => setTitulo(e.target.value)}
                placeholder="Ej. Tenis deportivos"
              />
            </Field>

            <Field label="Estado">
              <div style={S.checkboxCard}>
                <label style={S.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={!esUsado}
                    onChange={(e) => setEsUsado(!e.target.checked)}
                  />
                  <span style={S.checkboxText}>Nuevo</span>
                </label>
                <div style={S.hintMini}>
                  Si está marcado, el producto se guarda como nuevo.
                </div>
              </div>
            </Field>
          </Row2>

          <Field label="Descripción *">
            <textarea
              style={{ ...S.input, minHeight: 110, resize: "vertical" }}
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              placeholder="Describe condición, detalles, entrega, etc."
            />
          </Field>

          <Row2>
            <Field label="Garantía (opcional)">
              <input
                style={S.input}
                value={garantia}
                onChange={(e) => setGarantia(e.target.value)}
                placeholder="Si queda vacío: Sin garantía"
              />
            </Field>

            <Field label="Precio (lo que tú recibes) *">
              <input
                style={S.input}
                inputMode="decimal"
                value={precioVendedor}
                onChange={(e) => setPrecioVendedor(e.target.value)}
                placeholder="Ej. 1200"
              />
            </Field>
          </Row2>

          <div style={S.priceBox}>
            <div style={S.priceLine}>
              <b>Comisión 15%</b>
              <span>{comision == null ? "—" : comision.toFixed(2)}</span>
            </div>
            <div style={S.priceLine}>
              <b>Precio de venta</b>
              <span>{precioVenta == null ? "—" : precioVenta.toFixed(2)}</span>
            </div>
          </div>
        </Section>

       <Section title="Costo de envío (límites automáticos)">
          <Row2>
            <Field label={`Tamaño ${limiteLargo ? `(límite: ${limiteLargo})` : ""}`}>
              <input
                style={S.input}
                inputMode="decimal"
                value={tamanoCm}
                onChange={(e) => setTamanoCm(e.target.value)}
                placeholder="Ej. 60"
              />
            </Field>

            <Field label={`Peso ${limitePeso ? `(límite: ${limitePeso})` : ""}`}>
              <input
                style={S.input}
                inputMode="decimal"
                value={pesoKg}
                onChange={(e) => setPesoKg(e.target.value)}
                placeholder="Ej. 3.5"
              />
            </Field>
          </Row2>

          <div style={S.notice}>
            <div style={S.switchRow}>
              <span style={{ fontWeight: 900 }}>¿Quieres que lo enviemos por ti?</span>
              <label style={S.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={nosotrosEnviamos && !excedeLimite}
                  disabled={excedeLimite}
                  onChange={(e) => {
                    if (!excedeLimite) {
                      setNosotrosEnviamos(e.target.checked);
                    }
                  }}
                />
              </label>
            </div>

            {excedeLimite ? (
              <div style={{ marginTop: 10 }}>
                <div style={{ color: "#b42318", fontWeight: 800 }}>
                  Por el momento no podemos enviar este producto.
                </div>
                <div style={{ marginTop: 6, color: "#b42318" }}>
                  Revisa peso y tamaño permitidos.
                </div>
              </div>
            ) : null}

            {(!nosotrosEnviamos || excedeLimite) ? (
              <div style={{ marginTop: 12 }}>
                <div style={S.switchRow}>
                  <span style={{ fontWeight: 900 }}>Envío gratis</span>
                  <label style={S.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={envioGratisTienda}
                      onChange={(e) => setEnvioGratisTienda(e.target.checked)}
                    />
                  </label>
                </div>

                {!envioGratisTienda ? (
                  <div style={{ marginTop: 10 }}>
                    <Field label="Tu costo de envío (MXN) *">
                      <input
                        style={S.input}
                        inputMode="decimal"
                        value={costoEnvioTienda}
                        onChange={(e) => setCostoEnvioTienda(e.target.value)}
                        placeholder="Ej. 180"
                      />
                    </Field>
                  </div>
                ) : (
                  <div style={{ marginTop: 8, ...S.hint }}>
                    Se mostrará como envío gratis.
                  </div>
                )}
              </div>
            ) : (
              <div style={{ marginTop: 10, ...S.okBox }}>
                Listo: nosotros enviaremos este producto.
              </div>
            )}
          </div>
        </Section>

        <Section title="Categorías (máximo 3)">
          <div style={S.catsGrid}>
            {cats.map((c) => {
              const active = catSel.includes(c.nombre);
              const disabled = !active && catSel.length >= MAX_CATS;

              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => {
                    if (active) setCatSel((prev) => prev.filter((x) => x !== c.nombre));
                    else if (!disabled) setCatSel((prev) => [...prev, c.nombre]);
                  }}
                  disabled={disabled}
                  style={{
                    ...S.catChip,
                    ...(active ? S.catChipOn : {}),
                    ...(disabled ? { opacity: 0.4, cursor: "not-allowed" } : {}),
                  }}
                  title={c.nombre}
                >
                  {c.nombre}
                </button>
              );
            })}
          </div>
          <div style={S.hint}>
            Seleccionadas: <b>{catSel.length}</b> / {MAX_CATS}
          </div>
        </Section>

        <Section title="Imagen principal">
          <div style={S.imgRow}>
            <div style={S.imgBox}>
              {principalPreview ? (
                <img src={principalPreview} alt="preview" style={S.img} />
              ) : (
                <div style={{ opacity: 0.7, fontWeight: 900 }}>Sin imagen</div>
              )}
            </div>

            <div style={{ flex: 1 }}>
              <div style={S.hint}>
                Se suben 2 versiones: <b>1200px</b> y <b>256px</b>.
              </div>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const f = e.target.files?.[0] || null;
                  setPrincipalFile(f);
                  if (f) setPrincipalPreview(URL.createObjectURL(f));
                }}
              />
              {isEdit ? (
                <div style={S.hint}>Si no cambias la imagen, se conserva la actual.</div>
              ) : null}
            </div>
          </div>
        </Section>

        <Section title="Variantes (color/talla/stock/imágenes)">
          <div style={S.variantTop}>
            <div style={{ display: "grid", gap: 10 }}>
              <div style={S.subTitle}>Colores</div>

              <div style={S.chips}>
                {colorPresets.map((c) => (
                  <button
                    type="button"
                    key={c}
                    onClick={() => toggleInList(c, selColors, setSelColors)}
                    style={{ ...S.chip, ...(selColors.includes(c) ? S.chipOn : {}) }}
                  >
                    {c}
                  </button>
                ))}
              </div>

              <div style={S.addRow}>
                <input
                  style={S.input}
                  value={customColor}
                  onChange={(e) => setCustomColor(e.target.value)}
                  placeholder="Agregar color…"
                />
                <button type="button" style={S.secondaryBtn} onClick={addCustomColor}>
                  Agregar
                </button>
              </div>

              <div style={S.selectedWrap}>
                <div style={S.selectedTitle}>Colores agregados:</div>
                {selColors.length ? (
                  <div style={S.selectedChips}>
                    {selColors.map((c) => (
                      <div key={c} style={S.selectedChip}>
                        <span>{c}</span>
                        <button type="button" style={S.selectedChipX} onClick={() => removeSelectedColor(c)}>
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={S.hintMini}>Todavía no agregas colores.</div>
                )}
              </div>
            </div>

            <div style={{ display: "grid", gap: 10 }}>
              <div style={S.subTitle}>Tallas</div>

              <div style={S.chips}>
                {tallaPresets.map((t) => (
                  <button
                    type="button"
                    key={t}
                    onClick={() => toggleInList(t, selSizes, setSelSizes)}
                    style={{ ...S.chip, ...(selSizes.includes(t) ? S.chipOn : {}) }}
                  >
                    {t}
                  </button>
                ))}
              </div>

              <div style={S.addRow}>
                <input
                  style={S.input}
                  value={customSize}
                  onChange={(e) => setCustomSize(e.target.value)}
                  placeholder="Agregar talla…"
                />
                <button type="button" style={S.secondaryBtn} onClick={addCustomSize}>
                  Agregar
                </button>
              </div>

              <div style={S.selectedWrap}>
                <div style={S.selectedTitle}>Tallas agregadas:</div>
                {selSizes.length ? (
                  <div style={S.selectedChips}>
                    {selSizes.map((t) => (
                      <div key={t} style={S.selectedChip}>
                        <span>{t}</span>
                        <button type="button" style={S.selectedChipX} onClick={() => removeSelectedSize(t)}>
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={S.hintMini}>Todavía no agregas tallas.</div>
                )}
              </div>
            </div>
          </div>

          <div style={S.variantActions}>
            <button type="button" style={S.primaryBtn} onClick={generateVariants}>
              Generar variantes
            </button>
            <div style={S.hint}>
              Debes agregar al menos una variante para publicar el producto.
            </div>
          </div>

          {variants.length ? (
            <div style={{ marginTop: 12, display: "grid", gap: 12 }}>
              {variants.map((v) => (
                <div key={v.key} style={S.vCard}>
                  <div style={S.vHead}>
                    <div style={{ fontWeight: 950 }}>
                      {v.color || "Sin color"} • {v.talla || "Sin talla"}
                    </div>
                    <button type="button" style={S.dangerBtn} onClick={() => removeVariant(v.key)}>
                      Eliminar
                    </button>
                  </div>

                  <Row2>
                    <Field label="Stock *">
                      <input
                        style={S.input}
                        inputMode="numeric"
                        value={v.stock}
                        onChange={(e) => setVariantField(v.key, "stock", e.target.value)}
                        placeholder="Ej. 10"
                      />
                    </Field>

                    <Field label="Imágenes (1 a 5) *">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={(e) => addVariantFiles(v.key, e.target.files)}
                      />
                      <div style={S.hint}>Se suben 1200px y 256px por imagen.</div>
                    </Field>
                  </Row2>

                  {v.previews?.length ? (
                    <div style={S.prevGrid}>
                      {v.previews.map((src, idx) => (
                        <div key={idx} style={S.prevBox}>
                          <img src={src} alt="prev" style={S.prevImg} />
                          <button
                            type="button"
                            style={S.xBtn}
                            onClick={() => removeVariantImage(v.key, idx)}
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={S.hint}>Sin imágenes todavía.</div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div style={S.notice}>
              Debes generar y completar al menos una variante antes de publicar.
            </div>
          )}
        </Section>

        {error ? (
          <div style={S.errorBox}>
            <div style={S.errorTitle}>Revisa esto</div>
            <div style={S.errorText}>{error}</div>
          </div>
        ) : null}

        {saving ? (
          <div style={S.progressWrap}>
            <div style={S.progressTop}>
              <span style={{ fontWeight: 900 }}>Progreso del guardado</span>
              <span>{saveProgress}%</span>
            </div>

            <div style={S.progressBarBg}>
              <div style={{ ...S.progressBarFill, width: `${saveProgress}%` }} />
            </div>

            <div style={S.progressText}>{saveStep || "Procesando..."}</div>
          </div>
        ) : null}

        <div style={S.footerRow}>
          {isEdit ? (
            <button
              type="button"
              style={S.deleteBtn}
              onClick={handleDeleteProduct}
              disabled={saving}
              title="Eliminar producto"
            >
              Eliminar
            </button>
          ) : null}

          <button
            type="button"
            style={S.secondaryBtn}
            onClick={() => navigate("/misProductos")}
            disabled={saving}
          >
            Cancelar
          </button>

          <button type="button" style={S.primaryBtn} onClick={handleSave} disabled={saving}>
            {saving ? "Guardando…" : isEdit ? "Guardar cambios" : "Publicar producto"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={S.section}>
      <div style={S.sectionTitle}>{title}</div>
      <div style={{ marginTop: 10 }}>{children}</div>
    </div>
  );
}

function Row2({ children }) {
  return <div style={S.row2}>{children}</div>;
}

function Field({ label, children }) {
  return (
    <label style={S.label}>
      {label}
      {children}
    </label>
  );
}

const S = {
  page: { padding: 16 },
  card: {
    maxWidth: 980,
    margin: "0 auto",
    borderRadius: 18,
    border: "1px solid rgba(0,0,0,0.10)",
    background: "rgba(255,255,255,0.92)",
    boxShadow: "0 18px 65px rgba(0,0,0,0.10)",
    padding: 16,
  },
  topRow: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 },
  h1: { fontSize: 22, fontWeight: 950, letterSpacing: -0.3 },
  sub: { marginTop: 6, fontSize: 13, opacity: 0.75, lineHeight: 1.5 },

  section: {
    marginTop: 14,
    borderRadius: 16,
    border: "1px solid rgba(0,0,0,0.10)",
    background: "rgba(0,0,0,0.02)",
    padding: 12,
  },
  sectionTitle: { fontWeight: 950, fontSize: 14 },

  row2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
  label: { fontSize: 12, opacity: 0.85, fontWeight: 900, display: "grid", gap: 8 },
  input: {
    padding: "12px 12px",
    borderRadius: 12,
    border: "1px solid rgba(0,0,0,0.12)",
    background: "rgba(255,255,255,0.95)",
    outline: "none",
    fontSize: 14,
  },

  checkboxCard: {
    borderRadius: 12,
    border: "1px solid rgba(0,0,0,0.10)",
    background: "rgba(255,255,255,0.88)",
    padding: 12,
  },
  checkboxLabel: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    fontWeight: 900,
    fontSize: 14,
  },
  checkboxText: {
    fontWeight: 950,
  },
  hintMini: {
    marginTop: 6,
    fontSize: 12,
    opacity: 0.72,
  },

  priceBox: {
    marginTop: 10,
    borderRadius: 14,
    border: "1px solid rgba(0,0,0,0.10)",
    background: "rgba(255,255,255,0.8)",
    padding: 12,
    display: "grid",
    gap: 8,
  },
  priceLine: { display: "flex", justifyContent: "space-between", fontWeight: 850 },

  hint: { marginTop: 8, fontSize: 12, opacity: 0.7 },

  notice: {
    marginTop: 12,
    borderRadius: 14,
    border: "1px solid rgba(255,165,0,0.25)",
    background: "rgba(255,165,0,0.08)",
    padding: 12,
  },
  okBox: {
    marginTop: 10,
    borderRadius: 14,
    border: "1px solid rgba(0,0,0,0.10)",
    background: "rgba(255,255,255,0.75)",
    padding: 12,
    fontSize: 13,
    opacity: 0.85,
    fontWeight: 800,
  },

  catsGrid: { display: "flex", flexWrap: "wrap", gap: 8 },
  catChip: {
    padding: "9px 10px",
    borderRadius: 999,
    border: "1px solid rgba(0,0,0,0.12)",
    background: "rgba(255,255,255,0.9)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 12,
  },
  catChipOn: {
    background: "rgba(17,24,39,0.92)",
    color: "white",
    border: "1px solid rgba(17,24,39,0.2)",
  },

  imgRow: { display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" },
  imgBox: {
    width: 160,
    height: 120,
    borderRadius: 14,
    border: "1px solid rgba(0,0,0,0.10)",
    background: "rgba(255,255,255,0.85)",
    display: "grid",
    placeItems: "center",
    overflow: "hidden",
  },
  img: { width: "100%", height: "100%", objectFit: "cover" },

  switchRow: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 },

  variantTop: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
  subTitle: { fontWeight: 950, opacity: 0.85 },
  chips: { display: "flex", gap: 8, flexWrap: "wrap" },
  chip: {
    padding: "8px 10px",
    borderRadius: 999,
    border: "1px solid rgba(0,0,0,0.12)",
    background: "rgba(255,255,255,0.9)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 12,
  },
  chipOn: { background: "rgba(90,120,255,0.18)", border: "1px solid rgba(90,120,255,0.28)" },
  addRow: { display: "grid", gridTemplateColumns: "1fr auto", gap: 10 },

  selectedWrap: {
    marginTop: 4,
    padding: 10,
    borderRadius: 12,
    border: "1px solid rgba(0,0,0,0.08)",
    background: "rgba(255,255,255,0.65)",
  },
  selectedTitle: {
    fontSize: 12,
    fontWeight: 900,
    marginBottom: 8,
    opacity: 0.78,
  },
  selectedChips: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },
  selectedChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 10px",
    borderRadius: 999,
    background: "rgba(90,120,255,0.10)",
    border: "1px solid rgba(90,120,255,0.20)",
    fontWeight: 800,
    fontSize: 12,
  },
  selectedChipX: {
    border: "none",
    background: "transparent",
    cursor: "pointer",
    fontWeight: 900,
    opacity: 0.8,
  },

  variantActions: {
    marginTop: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
  },

  vCard: {
    borderRadius: 16,
    border: "1px solid rgba(0,0,0,0.10)",
    background: "rgba(255,255,255,0.88)",
    padding: 12,
  },
  vHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 10,
  },

  prevGrid: { marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap" },
  prevBox: {
    position: "relative",
    width: 86,
    height: 64,
    borderRadius: 12,
    overflow: "hidden",
    border: "1px solid rgba(0,0,0,0.10)",
  },
  prevImg: { width: "100%", height: "100%", objectFit: "cover" },
  xBtn: {
    position: "absolute",
    top: 6,
    right: 6,
    width: 22,
    height: 22,
    borderRadius: 999,
    border: "0",
    background: "rgba(0,0,0,0.65)",
    color: "white",
    cursor: "pointer",
    fontWeight: 950,
    lineHeight: "22px",
  },

  errorBox: {
    marginTop: 12,
    borderRadius: 14,
    border: "1px solid rgba(255,60,60,0.22)",
    background: "rgba(255,60,60,0.08)",
    padding: 12,
  },
  errorTitle: { fontWeight: 950, fontSize: 13, marginBottom: 4 },
  errorText: { fontSize: 13, opacity: 0.85, lineHeight: 1.45 },

  progressWrap: {
    marginTop: 14,
    borderRadius: 14,
    border: "1px solid rgba(90,120,255,0.18)",
    background: "rgba(90,120,255,0.06)",
    padding: 12,
  },
  progressTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    marginBottom: 8,
    fontSize: 13,
  },
  progressBarBg: {
    width: "100%",
    height: 12,
    borderRadius: 999,
    background: "rgba(0,0,0,0.08)",
    overflow: "hidden",
  },
  progressBarFill: {
    height: "100%",
    borderRadius: 999,
    background: "linear-gradient(90deg, rgba(90,120,255,0.92), rgba(70,100,245,0.92))",
    transition: "width 0.25s ease",
  },
  progressText: {
    marginTop: 8,
    fontSize: 12,
    opacity: 0.8,
    fontWeight: 700,
  },

  footerRow: { marginTop: 14, display: "flex", justifyContent: "flex-end", gap: 10, flexWrap: "wrap" },

  primaryBtn: {
    padding: "12px 14px",
    borderRadius: 14,
    border: "1px solid rgba(90,120,255,0.25)",
    background: "linear-gradient(180deg, rgba(90,120,255,0.92), rgba(70,100,245,0.92))",
    color: "white",
    fontWeight: 950,
    cursor: "pointer",
    boxShadow: "0 18px 55px rgba(90,120,255,0.22)",
  },
  secondaryBtn: {
    padding: "10px 12px",
    borderRadius: 14,
    border: "1px solid rgba(0,0,0,0.12)",
    background: "rgba(255,255,255,0.95)",
    color: "rgba(16,17,20,0.86)",
    fontWeight: 950,
    cursor: "pointer",
    height: 42,
  },
  dangerBtn: {
    padding: "9px 10px",
    borderRadius: 12,
    border: "1px solid rgba(220,38,38,0.25)",
    background: "rgba(220,38,38,0.08)",
    color: "rgba(140,20,20,0.95)",
    fontWeight: 950,
    cursor: "pointer",
  },
  deleteBtn: {
    padding: "12px 14px",
    borderRadius: 14,
    border: "1px solid rgba(220,38,38,0.28)",
    background: "linear-gradient(180deg, rgba(239,68,68,0.92), rgba(220,38,38,0.92))",
    color: "white",
    fontWeight: 950,
    cursor: "pointer",
    boxShadow: "0 18px 55px rgba(220,38,38,0.18)",
  },
};